"use client";

import { useState, useEffect } from 'react'
import { ContentInput } from '@/components/content/ContentInput'
import { ContentEnrichment } from '@/components/content/ContentEnrichment'
import { ThemeProvider, useTheme } from '@/components/theme/theme-provider'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Copy, Edit, ArrowRight, CheckCircle, Sun, Moon } from 'lucide-react'
import { api } from '@/lib/api'
import { AnalyzeResponse } from '@/types'
import { GeneratedContentType, GeneratedContent } from '@/types/content'
import type { ExtendedAnalyzeResponse } from '@/types/analyze'
import { useToast } from '@/components/ui/use-toast'

const contentTypeToEndpoint = {
  [GeneratedContentType.BLOG]: 'BLOG',
  [GeneratedContentType.SOCIAL_POST]: 'SOCIAL_POST',
  [GeneratedContentType.YOUTUBE_SCRIPT]: 'YOUTUBE_SCRIPT',
  [GeneratedContentType.SHORT_VIDEO_SCRIPT]: 'SHORT_VIDEO_SCRIPT'
} as const;

function AppContent() {
  const [currentStep, setCurrentStep] = useState(1)
  const [isProcessing, setIsProcessing] = useState(false)
  const [url, setUrl] = useState('')
  const [isVerified, setIsVerified] = useState(false)
  const [content, setContent] = useState<ExtendedAnalyzeResponse | null>(null)
  const { theme, setTheme } = useTheme()
  const { toast } = useToast()

  const handleContentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Get the URL from the form
    const formData = new FormData(e.target as HTMLFormElement);
    const url = formData.get('url') as string;

    // Validate URL
    if (!url?.trim()) {
      console.warn('⚠️ No URL provided');
      toast({
        variant: "destructive",
        title: "Error",
        description: "Please enter a valid URL",
      });
      return;
    }

    // Prevent double submission
    if (isProcessing) {
      console.warn('⚠️ Already processing');
      return;
    }

    console.log('🎬 Content submission started:', {
      url,
      timestamp: new Date().toISOString()
    });

    setIsProcessing(true);
    try {
      console.log('🔄 Processing content...');
      const response = await api.analyzeUrl(url);
      
      console.log('✅ Content processed:', {
        success: response.success,
        contentId: response.storedContentId,
        type: response.type,
        hasTranscript: !!response.content?.transcript,
        timestamp: new Date().toISOString()
      });

      if (response.success) {
        setContent(response);
        setCurrentStep(2);
        console.log('👉 Moving to step 2: Content Enrichment');
        toast({
          title: "Content processed successfully",
          description: "Your content has been analyzed and is ready for enrichment.",
        });
      }
    } catch (error) {
      console.error('💥 Content processing error:', {
        error: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined,
        timestamp: new Date().toISOString()
      });
      
      toast({
        variant: "destructive",
        title: "Error processing content",
        description: error instanceof Error ? error.message : "An error occurred while processing your content.",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  // Log step changes
  useEffect(() => {
    console.log('📍 Step changed:', {
      step: currentStep,
      timestamp: new Date().toISOString()
    });
  }, [currentStep]);

  // Log content changes
  useEffect(() => {
    if (content) {
      console.log('📄 Content updated:', {
        contentId: content.storedContentId,
        type: content.type,
        timestamp: new Date().toISOString()
      });
    }
  }, [content]);

  return (
    <div className="min-h-screen p-8 space-y-8 bg-background">
      {/* Theme Toggle */}
      <Button
        variant="outline"
        size="icon"
        className="fixed top-4 right-4 rounded-full"
        onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
      >
        {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
      </Button>

      {/* Progress Bar */}
      <div className="sticky top-0 z-10 pb-4 bg-background">
        <div className="flex justify-between mb-2">
          <Badge variant="default">1. Content Input</Badge>
          <Badge variant={currentStep >= 2 ? "default" : "outline"}>2. Content Enrichment</Badge>
          <Badge variant={currentStep >= 3 ? "default" : "outline"}>3. Content Generation</Badge>
        </div>
        <div className="w-full bg-muted h-2 rounded-full">
          <div 
            className="bg-primary h-2 rounded-full transition-all"
            style={{ width: `${(currentStep / 3) * 100}%` }}
          />
        </div>
      </div>

      {/* Step 1: Content Input */}
      <Card>
        <CardHeader>
          <CardTitle>Original Content</CardTitle>
          <CardDescription>View and analyze your content</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <form onSubmit={handleContentSubmit} className="flex gap-4">
              <Input 
                placeholder="Paste your content URL here" 
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                className="flex-1"
                name="url"
                type="url"
                required
                disabled={isProcessing}
              />
              <Button type="submit" disabled={!url || isProcessing}>
                {isProcessing ? 'Processing...' : 'Process'} 
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </form>
            
            <Tabs defaultValue="script">
              <TabsList>
                <TabsTrigger value="script">Script</TabsTrigger>
                <TabsTrigger value="summary">Summary</TabsTrigger>
                <TabsTrigger value="keypoints">Key Points</TabsTrigger>
              </TabsList>
              
              {["script", "summary", "keypoints"].map((tab) => (
                <TabsContent key={tab} value={tab}>
                  <div className="bg-muted p-4 rounded-md min-h-[200px] relative">
                    <p className="text-muted-foreground">
                      {tab === "script" && (content?.content?.transcript || "Original script content...")}
                      {tab === "summary" && (content?.keyPoints?.summary || "Content summary...")}
                      {tab === "keypoints" && (
                        content?.keyPoints?.keyPoints ? (
                          <ul className="list-disc pl-4 space-y-2">
                            {content.keyPoints.keyPoints.map((point, index) => (
                              <li key={index}>{point}</li>
                            ))}
                          </ul>
                        ) : "Key points..."
                      )}
                    </p>
                    <Button variant="ghost" size="icon" className="absolute top-2 right-2">
                      <Copy size={16} />
                    </Button>
                  </div>
                </TabsContent>
              ))}
            </Tabs>
          </div>
        </CardContent>
      </Card>

      {/* Step 2: Content Enrichment */}
      {currentStep >= 2 && content && (
        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <div>
                <CardTitle>Content Enrichment</CardTitle>
                <CardDescription>Choose enrichment options and verify results</CardDescription>
              </div>
              <Button 
                onClick={() => {
                  setIsVerified(true)
                  setCurrentStep(3)
                }}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                <CheckCircle className="mr-2 h-4 w-4" /> 
                Verify & Proceed
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <ContentEnrichment 
              onVerify={(enrichedContentId) => {
                setContent(prev => prev ? { ...prev, storedContentId: enrichedContentId } : null);
                setCurrentStep(3);
              }} 
              contentId={content.storedContentId || content.id} 
            />
          </CardContent>
        </Card>
      )}

      {/* Step 3: Content Generation */}
      {currentStep >= 3 && content?.storedContentId && (
        <div className="space-y-4">
          {Object.values(GeneratedContentType).map((type: GeneratedContentType) => {
            const displayType = type.toLowerCase().replace('_', ' ');
            
            return (
              <Card key={type}>
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <CardTitle className="text-lg">{displayType}</CardTitle>
                    <Button
                      variant="default"
                      size="sm"
                      className="w-24 text-sm"
                      onClick={async () => {
                        if (!content.storedContentId) return;

                        toast({
                          title: "Generating content",
                          description: `Generating ${displayType}...`,
                        });

                        try {
                          const response = await api.generateContent(content.storedContentId, type);
                          
                          if (response.success && response.data) {
                            // Get the first generated content from the response data
                            const generatedContent = Object.values(response.data)[0];
                            if (!generatedContent) {
                              throw new Error('No content generated');
                            }

                            toast({
                              title: "Content generated",
                              description: `Generated ${generatedContent.metadata.wordCount} words of ${displayType}`,
                            });

                            setContent((prev: ExtendedAnalyzeResponse | null) => {
                              if (!prev) return null;
                              const updatedContent: ExtendedAnalyzeResponse = {
                                ...prev,
                                generatedContent: {
                                  ...(prev.generatedContent || {}),
                                  [type]: generatedContent
                                }
                              };
                              return updatedContent;
                            });
                          } else {
                            throw new Error('Failed to generate content');
                          }
                        } catch (error) {
                          console.error('Generation error:', error);
                          toast({
                            variant: "destructive",
                            title: "Generation failed",
                            description: error instanceof Error ? error.message : "Failed to generate content",
                          });
                        }
                      }}
                    >
                      Generate
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="bg-muted p-4 rounded-md min-h-[200px] relative">
                    <p className="text-muted-foreground whitespace-pre-wrap">
                      {content.generatedContent?.[type]?.content || `Generated ${displayType.toLowerCase()} will appear here...`}
                    </p>
                    <div className="absolute top-2 right-2 flex gap-2">
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={async () => {
                          const contentToCopy = content.generatedContent?.[type]?.content;
                          if (contentToCopy) {
                            try {
                              await navigator.clipboard.writeText(contentToCopy);
                              toast({
                                title: "Copied to clipboard",
                                description: "The generated content has been copied to your clipboard.",
                              });
                            } catch (error) {
                              toast({
                                variant: "destructive",
                                title: "Failed to copy",
                                description: "Could not copy the content to clipboard.",
                              });
                            }
                          }
                        }}
                      >
                        <Copy size={16} />
                      </Button>
                      <Button variant="ghost" size="icon">
                        <Edit size={16} />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  )
}

export default function App() {
  return (
    <ThemeProvider defaultTheme="dark" storageKey="ui-theme">
      <AppContent />
    </ThemeProvider>
  )
}
